export const DEFAULT_CONFIDENCE_THRESHOLDS = [
  {
    level: 'low',
    minValue: '0.00',
    maxValue: '0.49',
    backgroundColor: '#D32F2F',
    textColor: '#FFFFFF',
    isActive: true,
  },
  {
    level: 'medium',
    minValue: '0.50',
    maxValue: '0.84',
    backgroundColor: '#FBC02D',
    textColor: '#1F1F1F',
    isActive: true,
  },
  {
    level: 'high',
    minValue: '0.85',
    maxValue: '1.00',
    backgroundColor: '#2E7D32',
    textColor: '#FFFFFF',
    isActive: true,
  },
]

const LEGACY_KEY_MAP = {
  businessFront: 'business_front',
  paymentResponsible: 'payment_responsible',
}

const getRawSuggestion = (aiSuggestions, key) =>
  aiSuggestions?.[key] ?? aiSuggestions?.[LEGACY_KEY_MAP[key]]

const normalizeList = value => {
  if (!value) return []

  const normalizeItems = items => [...new Set(items.map(item => item.trim()).filter(Boolean))]

  if (Array.isArray(value)) {
    return normalizeItems(value.flatMap(item => normalizeList(item)))
  }

  if (typeof value === 'object') {
    return normalizeItems(Object.values(value).flatMap(item => normalizeList(item)))
  }

  return normalizeItems(String(value).split(/[|,;\n]/))
}

const parseDecimal = value => {
  if (value === null || value === undefined || value === '') return null

  const parsed = Number(String(value).replace('%', '').replace(',', '.').trim())
  if (Number.isNaN(parsed)) return null

  if (parsed > 1 && parsed <= 100) return parsed / 100
  if (parsed >= 0 && parsed <= 1) return parsed

  return null
}

export const normalizeConfidence = confidence => parseDecimal(confidence)

export const getAISuggestionValue = (aiSuggestions, key) => {
  const suggestion = getRawSuggestion(aiSuggestions, key)

  if (suggestion && typeof suggestion === 'object' && !Array.isArray(suggestion)) {
    return suggestion.value
  }

  return suggestion
}

export const getAISuggestionConfidence = (aiSuggestions, key) => {
  const suggestion = getRawSuggestion(aiSuggestions, key)

  if (!suggestion || typeof suggestion !== 'object' || Array.isArray(suggestion)) {
    return null
  }

  return normalizeConfidence(suggestion.confidence)
}

export const getAISuggestionTriggers = (aiSuggestions, key) => {
  const suggestion = getRawSuggestion(aiSuggestions, key)

  if (!suggestion || typeof suggestion !== 'object' || Array.isArray(suggestion)) {
    return []
  }

  return normalizeList(
    suggestion.trigger ??
      suggestion.triggers ??
      suggestion.triggerWords ??
      suggestion.trigger_words ??
      suggestion.keywords
  )
}

export const getAISuggestionReasoningSummary = (aiSuggestions, key) => {
  const suggestion = getRawSuggestion(aiSuggestions, key)

  if (!suggestion || typeof suggestion !== 'object' || Array.isArray(suggestion)) {
    return ''
  }

  return (
    suggestion.reasoningSummary ??
    suggestion.reasoning_summary ??
    suggestion.reasoning ??
    suggestion.explanation ??
    ''
  )
}

export const getAISuggestionCategoryNames = aiSuggestions => {
  const value = getAISuggestionValue(aiSuggestions, 'category')

  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value !== 'string') return []

  const delimiter = value.includes('|') ? '|' : ','
  return value
    .split(delimiter)
    .map(item => item.trim())
    .filter(Boolean)
}

export const formatConfidencePercentage = confidence => {
  const normalizedConfidence = normalizeConfidence(confidence)
  if (normalizedConfidence === null) return null

  return `${Math.round(normalizedConfidence * 100)}%`
}

export const findConfidenceThreshold = (confidence, thresholds = []) => {
  const normalizedConfidence = normalizeConfidence(confidence)
  if (normalizedConfidence === null) return null

  return thresholds
    ?.filter(threshold => threshold?.isActive !== false)
    ?.find(threshold => {
      const minValue = parseDecimal(threshold.minValue)
      const maxValue = parseDecimal(threshold.maxValue)

      return (
        minValue !== null &&
        maxValue !== null &&
        normalizedConfidence >= minValue &&
        normalizedConfidence <= maxValue
      )
    })
}

export const getConfidenceBadgeConfig = (confidence, thresholds = []) => {
  const label = formatConfidencePercentage(confidence)
  if (!label) return null

  const availableThresholds = thresholds?.length ? thresholds : DEFAULT_CONFIDENCE_THRESHOLDS
  const threshold = findConfidenceThreshold(confidence, availableThresholds)

  return {
    label,
    backgroundColor: threshold?.backgroundColor || '#6B7280',
    textColor: threshold?.textColor || '#FFFFFF',
    level: threshold?.level || null,
  }
}
